import { useEffect, useRef, useState } from "react";

const DEFAULT_MIN_VISIBLE_MS = 500;

/**
 * Smooths a loading flag so loading UI (skeletons, spinners) never flashes:
 * once the flag turns on, the returned value stays on for at least
 * `minVisibleMs`, even if the underlying fetch finishes sooner.
 */
export function useMinimumVisibleLoading(
    isLoading: boolean,
    minVisibleMs: number = DEFAULT_MIN_VISIBLE_MS,
): boolean {
    const [isVisible, setIsVisible] = useState(isLoading);
    const shownAtRef = useRef<number | null>(null);

    useEffect(() => {
        if (isLoading) {
            if (shownAtRef.current === null) {
                shownAtRef.current = Date.now();
            }
            setIsVisible(true);
            return;
        }

        if (shownAtRef.current === null) {
            setIsVisible(false);
            return;
        }

        const elapsedMs = Date.now() - shownAtRef.current;
        const remainingMs = minVisibleMs - elapsedMs;
        if (remainingMs <= 0) {
            shownAtRef.current = null;
            setIsVisible(false);
            return;
        }

        const timeoutId = setTimeout(() => {
            shownAtRef.current = null;
            setIsVisible(false);
        }, remainingMs);
        return () => clearTimeout(timeoutId);
    }, [isLoading, minVisibleMs]);

    // `isVisible` lags one render behind (it updates in an effect); OR-ing the
    // raw flag makes the rising edge instant while the hold governs falloff.
    return isLoading || isVisible;
}
